/**
 * Portal-member admin hooks (P30 / F).
 *   GET    /api/v1/me                — current user info
 *   GET    /api/v1/portal-members    — list (admin only)
 *   POST   /api/v1/portal-members    — add or update (admin only)
 *   DELETE /api/v1/portal-members/:email — remove (admin only)
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client.js';

export interface PortalMemberRow {
  email: string;
  display_name: string | null;
  displayName?: string | null;
  role: 'admin' | 'viewer';
  added_by?: string | null;
  addedBy?: string | null;
  added_at: string;
  addedAt?: string;
  last_login_at: string | null;
  lastLoginAt?: string | null;
}

interface ListResponse {
  items: PortalMemberRow[];
}

export function usePortalMembers() {
  return useQuery({
    queryKey: ['portal-members'],
    queryFn: () => apiFetch<ListResponse>('/api/v1/portal-members'),
  });
}

export function useUpsertPortalMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; display_name?: string; role: 'admin' | 'viewer' }) =>
      apiFetch<PortalMemberRow>('/api/v1/portal-members', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-members'] }),
  });
}

export function useDeletePortalMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<void>(`/api/v1/portal-members/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['portal-members'] }),
  });
}
